import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Apollo, gql } from 'apollo-angular';
import {
  AchAccountType,
  Address,
  Branding,
  CardBrand,
  CartItem,
  Merchant,
  PaymentInstrument,
  PaymentResult,
  PaymentStatus,
  Product,
  TaxEngine,
  WalletProvider,
} from '../domain/models';

/**
 * Real GraphQL-backed API for the checkout site (Unit 3). Drop-in replacement
 * for `MockApiService`: the three consumer-facing methods keep the same
 * `Promise`-returning shapes (`merchantBySlug`, `productBySku`,
 * `processPayment`) so pages swap the injected type and nothing else.
 *
 * Backed by Apollo (`app.config.ts` wires the AppSync endpoint + `x-api-key`).
 * `merchantBySlug`, `productBySku`, and `processPayment` are all public
 * (@aws_api_key on the server) — no Cognito login on the checkout site.
 *
 * The GraphQL schema is intentionally narrower than the app's presentation
 * model in two places, reconciled here rather than leaking into pages:
 *   • `Branding` on the wire has no `logoInitials`, and can be null entirely
 *     (a merchant that hasn't set colors). We synthesize initials from the
 *     name and fall back to the app's default palette (kept identical to
 *     styles.scss so an unbranded merchant renders exactly as before).
 *   • `Product` on the wire has no `icon`/`accent` — those are checkout-only
 *     presentation. We derive them deterministically from the sku so tiles
 *     stay stable across reloads without a per-merchant fixture.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly apollo = inject(Apollo);

  async merchantBySlug(slug: string): Promise<Merchant | null> {
    const res = await firstValueFrom(
      this.apollo.query<{ merchantBySlug: GqlMerchant | null }>({
        query: MERCHANT_BY_SLUG,
        variables: { slug },
        fetchPolicy: 'network-only',
      }),
    );
    const m = res.data?.merchantBySlug;
    return m ? mapMerchant(m) : null;
  }

  async productBySku(merchantSlug: string, sku: string): Promise<Product | null> {
    const res = await firstValueFrom(
      this.apollo.query<{ productBySku: GqlProduct | null }>({
        query: PRODUCT_BY_SKU,
        variables: { merchantSlug, sku },
        fetchPolicy: 'network-only',
      }),
    );
    const p = res.data?.productBySku;
    return p ? mapProduct(p) : null;
  }

  /** Public: list a merchant's active products by slug (hosted storefront). */
  async productsBySlug(merchantSlug: string): Promise<Product[]> {
    const res = await firstValueFrom(
      this.apollo.query<{ productsBySlug: GqlProduct[] }>({
        query: PRODUCTS_BY_SLUG,
        variables: { merchantSlug },
        fetchPolicy: 'network-only',
      }),
    );
    return (res.data?.productsBySlug ?? []).map(mapProduct);
  }

  /**
   * Process a WEB payment. Extends the mock's input with the cart `items`
   * (the server-side `ProcessPaymentInput.items` is required and drives the
   * authoritative subtotal/tax/shipping totals + shipping enforcement).
   *
   * The card branch is always `manual` for the web checkout: the CardSecure
   * hosted iframe returns a token + MMYY expiry, which map to
   * `ManualCardInput { cardToken, expiryMonth, expiryYear, cardholderName }`.
   * The raw PAN never reaches this layer (SAQ A).
   */
  async processPayment(input: ProcessPaymentInput): Promise<PaymentResult> {
    const exp = parseExpiry(input.expiry);
    if (!exp) {
      // No usable expiry means we cannot build a valid ManualCardInput
      // (expiryMonth/expiryYear are non-null on the server). Fail closed —
      // never send a malformed charge — and surface a retryable ERROR.
      return {
        status: 'ERROR',
        errorMessage:
          "We couldn't read the card's expiry date. No charge was made. Please re-enter the card and try again.",
      };
    }

    const gqlInput: GqlProcessPaymentInput = {
      merchantSlug: input.merchantSlug,
      source: 'WEB',
      items: input.items.map((it) => ({ sku: it.sku, quantity: it.quantity })),
      card: {
        manual: {
          cardToken: input.cardToken,
          expiryMonth: exp.month,
          expiryYear: exp.year,
          cardholderName: input.name,
        },
      },
      idempotencyKey: crypto.randomUUID(),
    };
    if (input.shipping) {
      gqlInput.shipping = {
        shipTo: mapAddressInput(input.shipping.shipTo),
        amount: input.shipping.amountCents,
        carrierKind: input.shipping.carrierKind,
      };
    }

    const res = await firstValueFrom(
      this.apollo.mutate<{ processPayment: GqlPaymentResult }>({
        mutation: PROCESS_PAYMENT,
        variables: { input: gqlInput },
      }),
    );
    const r = res.data?.processPayment;
    if (!r) {
      return {
        status: 'ERROR',
        errorMessage:
          "We couldn't reach the payment processor. No charge was made. Please try again in a moment.",
      };
    }
    return mapPaymentResult(r);
  }
}

// ---------------------------------------------------------------------------
// Consumer-facing input types (mirror the mock's, extended with `items`).
// ---------------------------------------------------------------------------

export interface ProcessPaymentInput {
  readonly merchantSlug: string;
  /** CardSecure token from the hosted tokenizer — never the raw PAN. */
  readonly cardToken: string;
  /** Card expiry (MMYY) as reported by the tokenizer, if provided. */
  readonly expiry: string | null;
  readonly name: string;
  /**
   * Buyer email + ZIP are collected on the form for UX, but the current
   * server `ProcessPaymentInput` has no field for them, so they are not
   * transmitted. Kept on the input so pages don't change shape.
   */
  readonly email: string;
  readonly zip: string;
  /** Cart line items — required by the server; drives authoritative totals. */
  readonly items: readonly CartItem[];
  readonly totalCents: number;
  readonly subtotalCents: number;
  readonly taxCents: number;
  /** Present when any cart item requires shipping. */
  readonly shipping?: ShippingPayload;
}

export interface ShippingPayload {
  readonly shipTo: Address;
  readonly amountCents: number;
  /** Carrier plugin id — 'manual' for POC. */
  readonly carrierKind: string;
}

// ---------------------------------------------------------------------------
// GraphQL documents
// ---------------------------------------------------------------------------

const MERCHANT_BY_SLUG = gql`
  query MerchantBySlug($slug: String!) {
    merchantBySlug(slug: $slug) {
      id
      slug
      name
      returnUrl
      branding {
        logoUrl
        backgroundImageUrl
        primaryColor
        secondaryColor
        tertiaryColor
        surfaceColor
      }
      taxEngine {
        kind
        config
      }
    }
  }
`;

const PRODUCT_BY_SKU = gql`
  query ProductBySku($merchantSlug: String!, $sku: String!) {
    productBySku(merchantSlug: $merchantSlug, sku: $sku) {
      id
      sku
      merchantId
      name
      description
      priceAmount
      priceCurrency
      imageUrl
      requiresShipment
    }
  }
`;

const PRODUCTS_BY_SLUG = gql`
  query ProductsBySlug($merchantSlug: String!) {
    productsBySlug(merchantSlug: $merchantSlug) {
      id
      sku
      merchantId
      name
      description
      priceAmount
      priceCurrency
      imageUrl
      requiresShipment
    }
  }
`;

const PROCESS_PAYMENT = gql`
  mutation ProcessPayment($input: ProcessPaymentInput!) {
    processPayment(input: $input) {
      status
      transactionId
      declineReason
      errorMessage
      amountAmount
      amountCurrency
      subtotalAmount
      taxAmount
      shippingAmount
      processorReference
      instrument {
        __typename
        ... on Card {
          brand
          last4
          expiryMonth
          expiryYear
        }
        ... on BankAccount {
          bankName
          accountLast4
          routingLast4
          accountType
        }
        ... on WalletToken {
          provider
          deviceSuffix
          underlyingBrand
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Wire types (what the schema returns)
// ---------------------------------------------------------------------------

interface GqlBranding {
  readonly logoUrl: string | null;
  readonly backgroundImageUrl: string | null;
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly tertiaryColor: string;
  readonly surfaceColor: string;
}

interface GqlMerchant {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly returnUrl: string | null;
  readonly branding: GqlBranding | null;
  /** `config` arrives as a JSON-scalar STRING from AppSync (e.g. '{"rate":0.08}'). */
  readonly taxEngine: { readonly kind: string; readonly config: unknown };
}

interface GqlProduct {
  readonly id: string;
  readonly sku: string;
  readonly merchantId: string;
  readonly name: string;
  readonly description: string | null;
  readonly priceAmount: number;
  readonly priceCurrency: string;
  readonly imageUrl: string | null;
  readonly requiresShipment: boolean;
}

interface GqlPaymentResult {
  readonly status: PaymentStatus;
  readonly transactionId: string | null;
  readonly declineReason: string | null;
  readonly errorMessage: string | null;
  readonly amountAmount: number | null;
  readonly amountCurrency: string | null;
  readonly subtotalAmount: number | null;
  readonly taxAmount: number | null;
  readonly shippingAmount: number | null;
  readonly processorReference: string | null;
  readonly instrument: GqlInstrument | null;
}

type GqlInstrument =
  | { readonly __typename: 'Card'; readonly brand: CardBrand; readonly last4: string; readonly expiryMonth: number | null; readonly expiryYear: number | null }
  | { readonly __typename: 'BankAccount'; readonly bankName: string | null; readonly accountLast4: string; readonly routingLast4: string | null; readonly accountType: AchAccountType }
  | { readonly __typename: 'WalletToken'; readonly provider: WalletProvider; readonly deviceSuffix: string | null; readonly underlyingBrand: CardBrand | null };

// ---------------------------------------------------------------------------
// GraphQL input types (what the mutation expects)
// ---------------------------------------------------------------------------

interface GqlCartItemInput {
  readonly sku: string;
  readonly quantity: number;
}

interface GqlAddressInput {
  readonly line1: string;
  readonly line2?: string;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
}

interface GqlProcessPaymentInput {
  merchantSlug: string;
  source: 'WEB' | 'READER' | 'MANUAL';
  items: GqlCartItemInput[];
  card: { manual: { cardToken: string; expiryMonth: number; expiryYear: number; cardholderName?: string } };
  shipping?: { shipTo: GqlAddressInput; amount: number; carrierKind: string };
  idempotencyKey?: string;
}

// ---------------------------------------------------------------------------
// Mappers: wire -> domain
// ---------------------------------------------------------------------------

/** App default palette — kept in sync with styles.scss `:root` defaults. */
const DEFAULT_BRANDING: Omit<Branding, 'logoInitials'> = {
  logoUrl: null,
  backgroundImageUrl: null,
  primaryColor: '#8B4A2B',
  secondaryColor: '#765848',
  tertiaryColor: '#65631C',
  surfaceColor: '#FFF8F5',
};

/** Placeholder accent gradients for product tiles (icon/accent aren't on the wire). */
const ACCENT_PALETTE: ReadonlyArray<readonly [string, string]> = [
  ['#E8C994', '#8B4A2B'],
  ['#F4D5A0', '#BE8D4A'],
  ['#C99064', '#5B2F15'],
  ['#B2BDA0', '#4C6B3E'],
  ['#9EC0D6', '#2F5A7A'],
  ['#D6A9C0', '#7A2F5A'],
];

function mapMerchant(m: GqlMerchant): Merchant {
  const initials = logoInitialsFromName(m.name);
  const branding: Branding = m.branding
    ? {
        logoInitials: initials,
        logoUrl: m.branding.logoUrl,
        backgroundImageUrl: m.branding.backgroundImageUrl,
        primaryColor: m.branding.primaryColor,
        secondaryColor: m.branding.secondaryColor,
        tertiaryColor: m.branding.tertiaryColor,
        surfaceColor: m.branding.surfaceColor,
      }
    : { logoInitials: initials, ...DEFAULT_BRANDING };

  return {
    id: m.id,
    slug: m.slug,
    name: m.name,
    returnUrl: m.returnUrl ?? null,
    branding,
    taxEngine: mapTaxEngine(m.taxEngine),
  };
}

function mapTaxEngine(t: { kind: string; config: unknown }): TaxEngine {
  return { kind: t.kind, config: parseJsonConfig(t.config) };
}

/** AppSync serializes the JSON scalar as a string; parse to an object. */
function parseJsonConfig(config: unknown): Record<string, unknown> {
  if (config && typeof config === 'object') return config as Record<string, unknown>;
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

function mapProduct(p: GqlProduct): Product {
  return {
    id: p.id,
    sku: p.sku,
    merchantId: p.merchantId,
    name: p.name,
    description: p.description ?? '',
    priceAmount: p.priceAmount,
    priceCurrency: p.priceCurrency,
    imageUrl: p.imageUrl,
    icon: p.requiresShipment ? 'local_shipping' : 'shopping_bag',
    accent: ACCENT_PALETTE[hashToIndex(p.sku, ACCENT_PALETTE.length)],
    requiresShipment: p.requiresShipment,
  };
}

function mapPaymentResult(r: GqlPaymentResult): PaymentResult {
  const base = {
    status: r.status,
    processorReference: r.processorReference ?? undefined,
    orderNumber: r.transactionId ?? undefined,
    instrument: r.instrument ? mapInstrument(r.instrument) : undefined,
    amountCents: r.amountAmount ?? undefined,
    subtotalCents: r.subtotalAmount ?? undefined,
    taxCents: r.taxAmount ?? undefined,
    shippingCents: r.shippingAmount ?? undefined,
  };
  if (r.status === 'APPROVED') {
    // The server PaymentResult carries no charge timestamp; stamp capture time
    // client-side so the receipt's "Date" row renders (mirrors the mock).
    return { ...base, chargedAt: new Date().toISOString() };
  }
  // DECLINED uses declineReason; ERROR uses errorMessage. Domain surfaces both
  // through `errorMessage`.
  return { ...base, errorMessage: r.errorMessage ?? r.declineReason ?? undefined };
}

function mapInstrument(i: GqlInstrument): PaymentInstrument {
  switch (i.__typename) {
    case 'Card':
      return {
        kind: 'CARD',
        brand: i.brand,
        last4: i.last4,
        ...(i.expiryMonth != null ? { expiryMonth: i.expiryMonth } : {}),
        ...(i.expiryYear != null ? { expiryYear: i.expiryYear } : {}),
      };
    case 'BankAccount':
      return {
        kind: 'BANK_ACCOUNT',
        ...(i.bankName != null ? { bankName: i.bankName } : {}),
        accountLast4: i.accountLast4,
        accountType: i.accountType,
        ...(i.routingLast4 != null ? { routingLast4: i.routingLast4 } : {}),
      };
    case 'WalletToken':
      return {
        kind: 'WALLET',
        provider: i.provider,
        ...(i.deviceSuffix != null ? { deviceSuffix: i.deviceSuffix } : {}),
        ...(i.underlyingBrand != null ? { underlyingBrand: i.underlyingBrand } : {}),
      };
  }
}

function mapAddressInput(a: Address): GqlAddressInput {
  return {
    line1: a.line1,
    ...(a.line2 ? { line2: a.line2 } : {}),
    city: a.city,
    region: a.region,
    postalCode: a.postalCode,
    country: a.country,
  };
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/**
 * Parse the tokenizer's expiry into a 1-12 month + 4-digit year, tolerant of the
 * formats CardConnect's itoke iframe can emit: `YYYYMM` (its enhancedresponse
 * default with a year dropdown), `MMYYYY`, `MMYY`, `YYMM`, and slash variants
 * (`MM/YY`, `MM/YYYY`). Returns null on anything unrecognisable so the caller
 * fails closed rather than sending a malformed charge.
 */
function parseExpiry(raw: string | null): { month: number; year: number } | null {
  if (!raw) return null;
  const s = raw.replace(/\D/g, '');

  // CardConnect itoke (year-dropdown mode) returns YYYY followed by a
  // NON-zero-padded month: "20272" = 2027-02, "202712" = 2027-12. Try that
  // year-first shape first (5 or 6 digits), then the usual fallbacks.
  const yearFirst = (str: string): { month: number; year: number } | null => {
    const year = Number(str.slice(0, 4));
    if (year < 2000 || year > 2099) return null;
    const month = Number(str.slice(4));
    if (!Number.isInteger(month) || month < 1 || month > 12) return null;
    return { month, year };
  };
  if (s.length === 5 || s.length === 6) {
    const yf = yearFirst(s);
    if (yf) return yf;
  }
  if (s.length === 6) {
    // MMYYYY (month first)
    const month = Number(s.slice(0, 2));
    const year = Number(s.slice(2, 6));
    if (month >= 1 && month <= 12 && year >= 2000 && year <= 2099) return { month, year };
  }
  if (s.length === 4) {
    // MMYY, else YYMM
    const mFirst = Number(s.slice(0, 2));
    if (mFirst >= 1 && mFirst <= 12) return { month: mFirst, year: 2000 + Number(s.slice(2, 4)) };
    const mLast = Number(s.slice(2, 4));
    if (mLast >= 1 && mLast <= 12) return { month: mLast, year: 2000 + Number(s.slice(0, 2)) };
  }
  return null;
}

function logoInitialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function hashToIndex(key: string, modulo: number): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % modulo;
}
